import React, { useRef, useEffect, useState, useCallback } from 'react';
import Webcam from 'react-webcam';
import jsQR from 'jsqr';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { AlertCircle, CheckCircle, Camera, X } from 'lucide-react';

const BarcodeScanner = ({ 
  isOpen, 
  onClose, 
  onBarcodeScanned, 
  title = "Scan Barcode",
  subtitle = "Point camera at barcode or QR code"
}) => {
  const webcamRef = useRef(null);
  const [scannedCode, setScannedCode] = useState(null);
  const [isScanning, setIsScanning] = useState(true);
  const [error, setError] = useState(null);
  const [hasPermission, setHasPermission] = useState(false);

  // Video constraints for better scanning
  const videoConstraints = {
    width: 640,
    height: 480,
    facingMode: { ideal: "environment" } // Use back camera on mobile
  };

  const scanBarcode = useCallback(() => {
    if (!webcamRef.current || !isScanning) return;

    try {
      const imageSrc = webcamRef.current.getScreenshot();
      if (!imageSrc) return;

      const image = new Image();
      image.src = imageSrc;
      image.onload = () => {
        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d');
        
        if (!context) return;

        canvas.width = image.width;
        canvas.height = image.height;
        context.drawImage(image, 0, 0, image.width, image.height);
        
        const imageData = context.getImageData(0, 0, canvas.width, canvas.height);
        const code = jsQR(imageData.data, canvas.width, canvas.height);
        
        if (code && code.data) {
          setScannedCode(code.data);
          setIsScanning(false);
          setError(null);
          
          // Call the parent callback with scanned code
          if (onBarcodeScanned) {
            onBarcodeScanned(code.data);
          }
        }
      };
    } catch (err) {
      console.error('Error scanning barcode:', err);
      setError('Error scanning barcode. Please try again.');
    }
  }, [isScanning, onBarcodeScanned]);

  // Scan every 500ms when scanning is active
  useEffect(() => {
    if (!isScanning || !isOpen) return;

    const interval = setInterval(scanBarcode, 500);
    return () => clearInterval(interval);
  }, [isScanning, isOpen, scanBarcode]);

  const handleUserMedia = () => {
    setHasPermission(true);
    setError(null);
  };

  const handleUserMediaError = (error) => {
    console.error('Camera access error:', error);
    setHasPermission(false);
    setError('Camera access denied. Please allow camera permissions to scan barcodes.');
  };

  const resetScanner = () => {
    setScannedCode(null);
    setIsScanning(true);
    setError(null);
  };

  const handleClose = () => {
    setScannedCode(null);
    setIsScanning(false);
    setError(null);
    if (onClose) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-md mx-auto">
        <CardHeader>
          <div className="flex justify-between items-center">
            <CardTitle className="flex items-center gap-2">
              <Camera className="w-5 h-5" />
              {title}
            </CardTitle>
            <Button onClick={handleClose} variant="ghost" size="sm">
              <X className="w-4 h-4" />
            </Button>
          </div>
          <p className="text-sm text-gray-600">{subtitle}</p>
        </CardHeader>
        
        <CardContent>
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md flex items-center gap-2">
              <AlertCircle className="w-4 h-4 text-red-500" />
              <span className="text-red-700 text-sm">{error}</span>
            </div>
          )}
          
          {scannedCode ? (
            <div className="space-y-4">
              <div className="p-4 bg-green-50 border border-green-200 rounded-md">
                <div className="flex items-center gap-2 mb-2">
                  <CheckCircle className="w-5 h-5 text-green-500" />
                  <span className="font-semibold text-green-700">Barcode Scanned Successfully!</span>
                </div>
                <p className="text-sm text-gray-700">Code: <span className="font-mono bg-gray-100 px-2 py-1 rounded">{scannedCode}</span></p>
              </div>
              
              <div className="flex gap-2">
                <Button onClick={resetScanner} variant="outline" className="flex-1">
                  Scan Another
                </Button>
                <Button onClick={handleClose} className="flex-1">
                  Done
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {hasPermission ? (
                <div className="relative">
                  <Webcam
                    ref={webcamRef}
                    screenshotFormat="image/png"
                    videoConstraints={videoConstraints}
                    onUserMedia={handleUserMedia}
                    onUserMediaError={handleUserMediaError}
                    className="w-full rounded-md"
                  />
                  
                  {isScanning && (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="border-2 border-blue-500 w-48 h-48 rounded-lg opacity-50"></div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="p-8 text-center bg-gray-50 rounded-md">
                  <Camera className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                  <p className="text-gray-600">Requesting camera access...</p>
                  <p className="text-sm text-gray-500 mt-2">Please allow camera permissions to scan barcodes</p>
                </div>
              )}
              
              {isScanning && hasPermission && (
                <div className="text-center">
                  <div className="inline-flex items-center gap-2 text-blue-600">
                    <div className="animate-pulse w-2 h-2 bg-blue-600 rounded-full"></div>
                    <span className="text-sm">Scanning for barcode...</span>
                  </div>
                </div>
              )}
              
              <div className="flex gap-2">
                <Button onClick={handleClose} variant="outline" className="flex-1">
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default BarcodeScanner;